import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import theme from '../../theme';
import * as api from './recipesApi';
import RecipeForm from './RecipeForm';
import RecipeCard from './RecipeCard';

var CATEGORY_TABS = [
  { id: 'all', label: 'All' },
  { id: 'breakfast', label: '🌅 Breakfast', color: theme.breakfast },
  { id: 'lunch', label: '☀️ Lunch', color: theme.lunch },
  { id: 'dinner', label: '🌙 Dinner', color: theme.dinner },
  { id: 'snack', label: '🍿 Snack', color: theme.snack },
  { id: 'side', label: '🥗 Side', color: theme.side },
];

var Page = styled.div`
  padding: 16px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  color: ${theme.text};
  @media (min-width: 600px) { padding: 24px; }
`;

var Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
`;

var Title = styled.h1`
  font-size: 1.5rem;
  color: ${theme.accent};
  margin: 0;
  @media (min-width: 600px) { font-size: 2rem; }
`;

var NewBtn = styled.button`
  padding: 14px 24px;
  border: none;
  border-radius: ${theme.radius};
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  background: ${theme.accentGradient};
  color: #000;
  min-height: 48px;
  white-space: nowrap;
  &:hover { transform: translateY(-2px); }
`;

var Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

var Tab = styled.button`
  padding: 10px 16px;
  border: 2px solid ${p => p.$active ? (p.$color || theme.accent) : theme.border};
  border-radius: ${theme.radiusPill};
  background: ${p => p.$active ? 'rgba(74,222,128,0.1)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : theme.textMuted};
  cursor: pointer;
  font-size: 0.9rem;
  min-height: 44px;
  white-space: nowrap;
  &:hover { border-color: ${p => p.$color || theme.accent}; }
`;

var SearchInput = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid ${theme.border};
  border-radius: ${theme.radius};
  font-size: 1rem;
  background: ${theme.bg};
  color: #fff;
  margin-bottom: 16px;
  min-height: 48px;
  &:focus { outline: none; border-color: ${theme.accent}; }
  &::placeholder { color: ${theme.textPlaceholder}; }
`;

var Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  @media (min-width: 600px) {
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 16px;
  }
`;

var Empty = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${theme.textDim};
  font-size: 1.1rem;
`;

var UserBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

var UserName = styled.span`
  color: ${theme.textMuted};
  font-size: 0.85rem;
`;

var LogoutBtn = styled.button`
  padding: 8px 16px;
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: transparent;
  color: #aaa;
  cursor: pointer;
  font-size: 0.85rem;
  min-height: 36px;
  &:hover { border-color: ${theme.error}; color: ${theme.error}; }
`;

var Toast = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 14px 24px;
  border-radius: 10px;
  background: ${p => p.$error ? theme.error : theme.success};
  color: #fff;
  z-index: 2000;
  font-weight: 500;
`;

var RecipesPage = function ({ user, onLogout, hideUserBar }) {
  var [recipes, setRecipes] = useState([]);
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('all');
  var [search, setSearch] = useState('');
  var [showForm, setShowForm] = useState(false);
  var [editRecipe, setEditRecipe] = useState(null);
  var [toast, setToast] = useState(null);

  var notify = function (msg, error) {
    setToast({ msg, error });
    setTimeout(function () { setToast(null); }, 2500);
  };

  var load = useCallback(async function () {
    try {
      setLoading(true);
      var data = await api.fetchRecipes();
      setRecipes(data);
    } catch (err) {
      if (err.message.includes('Session expired')) { onLogout(); return; }
      notify(err.message, true);
    } finally { setLoading(false); }
  }, [onLogout]);

  useEffect(function () { load(); }, [load]);

  var filtered = recipes.filter(function (r) {
    if (filter !== 'all' && r.category !== filter) return false;
    if (search) {
      var q = search.toLowerCase();
      return r.title?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q) || r.tags?.some(function (t) { return t.toLowerCase().includes(q); });
    }
    return true;
  });

  var handleSave = async function (data) {
    try {
      if (editRecipe) {
        await api.updateRecipe(editRecipe._id, data);
        notify('Recipe updated');
      } else {
        await api.createRecipe(data);
        notify('Recipe created');
      }
      setShowForm(false);
      setEditRecipe(null);
      load();
    } catch (err) { notify(err.message, true); }
  };

  var handleDelete = async function (recipe) {
    if (!window.confirm('Delete "' + recipe.title + '"?')) return;
    try {
      await api.deleteRecipe(recipe._id);
      notify('Recipe deleted');
      load();
    } catch (err) { notify(err.message, true); }
  };

  var handleToggleFavorite = async function (recipe) {
    try {
      await api.updateRecipe(recipe._id, { favorite: !recipe.favorite });
      load();
    } catch (err) { notify(err.message, true); }
  };

  return (
    <Page>
      {!hideUserBar && (
        <UserBar>
          <UserName>{user.username}</UserName>
          <LogoutBtn onClick={onLogout}>Sign Out</LogoutBtn>
        </UserBar>
      )}

      <Header>
        <Title>🥗 MealPrep</Title>
        <NewBtn onClick={function () { setEditRecipe(null); setShowForm(true); }}>+ New Recipe</NewBtn>
      </Header>

      <Tabs>
        {CATEGORY_TABS.map(function (t) {
          return (
            <Tab key={t.id} $active={filter === t.id} $color={t.color}
              onClick={function () { setFilter(t.id); }}>
              {t.label}
              {t.id !== 'all' ? ' (' + recipes.filter(function (r) { return r.category === t.id; }).length + ')' : ''}
            </Tab>
          );
        })}
      </Tabs>

      <SearchInput placeholder="Search recipes..." value={search}
        onChange={function (e) { setSearch(e.target.value); }} />

      {loading ? (
        <Empty>Loading...</Empty>
      ) : filtered.length === 0 ? (
        <Empty>{recipes.length === 0 ? 'No recipes yet. Tap "+ New Recipe" to start.' : 'No matching recipes.'}</Empty>
      ) : (
        <Grid>
          {filtered.map(function (recipe) {
            return (
              <RecipeCard key={recipe._id} recipe={recipe}
                onEdit={function () { setEditRecipe(recipe); setShowForm(true); }}
                onDelete={function () { handleDelete(recipe); }}
                onToggleFavorite={function () { handleToggleFavorite(recipe); }}
              />
            );
          })}
        </Grid>
      )}

      {showForm && (
        <RecipeForm recipe={editRecipe} onSave={handleSave}
          onClose={function () { setShowForm(false); setEditRecipe(null); }} />
      )}

      {toast && <Toast $error={toast.error}>{toast.msg}</Toast>}
    </Page>
  );
};

export default RecipesPage;
